/**
 * The Dashboard's date filter: relative presets down the left, a two-month
 * calendar for a custom span on the right — the shape of the vendor's picker,
 * rebuilt without a dependency.
 *
 * Presets apply on click. A custom span is picked start-then-end and waits
 * for Apply, so half a selection never reaches the charts. Future days are
 * disabled: the API clamps them anyway, and offering a day that will always
 * come back empty just manufactures a confusing zero.
 */

import { useEffect, useRef, useState } from 'react';
import { Calendar, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  RANGE_PRESETS,
  addMonths,
  displayDay,
  matchingPreset,
  monthGrid,
  monthOf,
  monthTitle,
  presetRange,
  rangeLabel,
  todayUtc,
  type DateRange,
  type Month,
} from '../../lib/date-range';
import { cn } from '../../lib/cn';

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

export function DateRangePicker({
  value,
  onChange,
}: {
  value: DateRange;
  onChange: (range: DateRange) => void;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // The custom draft: start-then-end, applied only on Apply.
  const [draftFrom, setDraftFrom] = useState<string | null>(null);
  const [draftTo, setDraftTo] = useState<string | null>(null);
  const [view, setView] = useState<Month>(addMonths(monthOf(value.to), -1));

  const today = todayUtc();
  const activePreset = matchingPreset(value);

  // Click-away closes and forgets the draft, same pattern as the header search.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const show = () => {
    setDraftFrom(value.from);
    setDraftTo(value.to);
    setView(addMonths(monthOf(value.to), -1));
    setOpen(true);
  };

  const pick = (day: string) => {
    if (!draftFrom || draftTo) {
      // Starting over: a fresh start point, end pending.
      setDraftFrom(day);
      setDraftTo(null);
    } else if (day < draftFrom) {
      // Clicked before the start — take it as the new start.
      setDraftFrom(day);
    } else {
      setDraftTo(day);
    }
  };

  const inDraft = (day: string): boolean => {
    if (!draftFrom) return false;
    const end = draftTo ?? draftFrom;
    return day >= draftFrom && day <= end;
  };

  const monthCal = (month: Month) => (
    <div key={monthTitle(month)} className="w-56">
      <div className="mb-1 text-center text-xs font-semibold text-ink-900">
        {monthTitle(month)}
      </div>
      <div className="grid grid-cols-7 gap-px">
        {WEEKDAYS.map((weekday) => (
          <div key={weekday} className="py-1 text-center text-2xs text-ink-400">
            {weekday}
          </div>
        ))}
        {monthGrid(month)
          .flat()
          .map((day, index) =>
            day === null ? (
              <div key={`pad-${index}`} />
            ) : (
              <button
                key={day}
                type="button"
                disabled={day > today}
                onClick={() => pick(day)}
                className={cn(
                  'rounded-sm py-1 text-center text-xs tabular transition-colors duration-fast',
                  day > today
                    ? 'text-ink-300'
                    : day === draftFrom || day === draftTo
                      ? 'bg-ink-900 font-semibold text-paper'
                      : inDraft(day)
                        ? 'bg-accent-50 text-accent-700'
                        : 'text-ink-700 hover:bg-surface-sunken',
                )}
              >
                {Number(day.slice(8))}
              </button>
            ),
          )}
      </div>
    </div>
  );

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : show())}
        aria-expanded={open}
        className="flex items-center gap-2 rounded-sm bg-surface px-2.5 py-1.5 text-xs font-semibold text-ink-900 shadow-hairline hover:bg-surface-sunken"
      >
        <Calendar size={13} strokeWidth={1.75} className="text-ink-400" aria-hidden />
        {rangeLabel(value)}
      </button>

      {open ? (
        <div className="absolute right-0 top-full z-50 mt-1 flex rounded border border-line bg-surface shadow-overlay">
          <div className="w-36 border-r border-line px-2 py-2">
            <div className="eyebrow px-2 pb-1.5">Relative</div>
            <ul className="space-y-px">
              {RANGE_PRESETS.map((preset) => (
                <li key={preset.key}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(presetRange(preset.key));
                      setOpen(false);
                    }}
                    className={cn(
                      'flex w-full items-center justify-between rounded px-2 py-1 text-left text-xs transition-colors duration-fast',
                      activePreset === preset.key
                        ? 'font-semibold text-ink-900'
                        : 'text-ink-500 hover:bg-surface-sunken hover:text-ink-900',
                    )}
                  >
                    {preset.label}
                    {activePreset === preset.key ? (
                      <Check size={12} strokeWidth={2.5} aria-hidden />
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="px-3 py-2">
            <div className="eyebrow pb-1.5">Custom date range</div>
            <div className="mb-2 flex items-center gap-2 text-xs">
              <span className="rounded-sm bg-surface-sunken px-2 py-1 tabular text-ink-900 shadow-hairline">
                {draftFrom ? displayDay(draftFrom) : '—'}
              </span>
              <span className="text-ink-400">→</span>
              <span className="rounded-sm bg-surface-sunken px-2 py-1 tabular text-ink-900 shadow-hairline">
                {draftTo ? displayDay(draftTo) : 'pick an end date'}
              </span>
            </div>

            <div className="flex items-start gap-4">
              <button
                type="button"
                aria-label="Earlier months"
                onClick={() => setView(addMonths(view, -1))}
                className="mt-16 rounded-full p-1 text-ink-400 hover:bg-surface-sunken hover:text-ink-900"
              >
                <ChevronLeft size={14} strokeWidth={1.75} aria-hidden />
              </button>
              {monthCal(view)}
              {monthCal(addMonths(view, 1))}
              <button
                type="button"
                aria-label="Later months"
                onClick={() => setView(addMonths(view, 1))}
                className="mt-16 rounded-full p-1 text-ink-400 hover:bg-surface-sunken hover:text-ink-900"
              >
                <ChevronRight size={14} strokeWidth={1.75} aria-hidden />
              </button>
            </div>

            <div className="mt-2 flex justify-end gap-2 border-t border-line pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-sm px-3 py-1 text-xs font-semibold text-ink-500 shadow-hairline hover:text-ink-900"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!draftFrom || !draftTo}
                onClick={() => {
                  if (draftFrom && draftTo) {
                    onChange({ from: draftFrom, to: draftTo });
                    setOpen(false);
                  }
                }}
                className="rounded-sm bg-ink-900 px-3 py-1 text-xs font-semibold text-paper disabled:opacity-40"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
